import { CatalogsApi } from 'SharedDir/backend/api';
import { makeRequest } from 'SharedDir/common/composables/useRequests';
import { Catalogs } from 'SharedDir/declarations/catalogs';
import { FieldValue, SelectFieldParams } from 'SharedDir/declarations/fields';
import { useCatalogStore } from 'SharedDir/stores/catalogs.store';
import { computed, ComputedRef, Ref, ref, toValue, watch } from 'vue';

export type SelectFieldValue = FieldValue<null | string | number | Array<number> | Array<string>>;

export const useCatalogsLoader = (
  refValue: Ref<SelectFieldValue>,
  params: SelectFieldParams | ComputedRef<SelectFieldParams>
) => {
  const loading = ref(false);
  const catalogValues = ref<{ value: string | number; label: string }[]>([]);
  const useCatalog = computed(() => typeof toValue(params).catalogSlug === 'string');
  const filter = ref('');

  const options = computed(() => {
    let list: { value: string | number; label: string }[];
    if (useCatalog.value) {
      list = catalogValues.value;
    } else {
      list = (toValue(params).values || []).map((value) => ({ value, label: value }));
    }

    if (toValue(params).useSearch && filter.value !== '') {
      return list.filter(({ label }) => label.toLowerCase().indexOf(filter.value) !== -1);
    }

    return list;
  });

  const requestOptions = {
    startLoading: () => {
      loading.value = true;
    },
    finishLoading: () => {
      loading.value = false;
    },
  };

  const loadCatalog = async (catalogSlug?: Catalogs.CatalogSlug | null) => {
    if (!catalogSlug) return;

    const catalogStore = useCatalogStore();

    const { values } = await makeRequest(
      // () => CatalogsApi.ValuesApi(catalogSlug).getAllValues(),
      () => catalogStore.fetchCatalogBySlug({ slug: catalogSlug }),
      requestOptions
    );
    catalogValues.value = values.map(({ id, name }) => ({ value: id as number, label: name }));
  };

  const setFilterProperty = (
    filterString: string,
    update: CallableFunction,
    abort: CallableFunction
  ) => {
    if (toValue(params).useSearch) {
      filter.value = filterString.toLowerCase();
      if (options.value.length > 0) {
        update();
      } else {
        abort();
      }
    } else {
      update();
    }
  };

  watch(
    () => toValue(params).catalogSlug,
    (newValue) => {
      loadCatalog(newValue);
    }
  );
  watch(
    () => toValue(params).multiple,
    (multiple) => {
      if (multiple && !Array.isArray(refValue.value.value)) {
        if (refValue.value.value !== null) {
          refValue.value.value = [refValue.value.value as any];
        }
      } else if (!multiple && Array.isArray(refValue.value.value)) {
        refValue.value.value = (([first]) => first || null)(refValue.value.value);
      }
    }
  );
  return {
    loading,
    catalogValues,
    useCatalog,
    filter,
    options,
    loadCatalog,
    setFilterProperty,
  };
};
